import type { ApiSyncCrmLevelPort } from '~/handlers/ports/api-sync-crm-level.port';
import type {
  CrmLevelSyncErrorKind,
  CrmLevelSyncResult,
  CrmLevelSyncStatus,
} from '~/types/crm-level-sync';
import { getApostatudoApiUrl } from '~/utils/apostatudo/get-apostatudo-api-url';
import { getApostatudoAuthHeaders } from '~/utils/apostatudo/get-apostatudo-auth-headers';

// Erro tipado no mesmo desenho do RouletteSpinError/DailyCheckinClaimError: o
// adapter só CLASSIFICA a recusa; quem decide o que fazer (aqui: nada na tela,
// só não repetir) é a store.
export class CrmLevelSyncError extends Error {
  constructor(
    public readonly kind: CrmLevelSyncErrorKind,
    message?: string,
  ) {
    super(message ?? kind);
    this.name = 'CrmLevelSyncError';
  }
}

// Os status que ESTE front sabe interpretar. Qualquer outro vira 'unknown' de
// propósito: numa rota que não corrige nada, um nome desconhecido não pode
// herdar o comportamento de um nome conhecido — o desenho antigo colapsava tudo
// em 'unchanged' e teria lido "divergência registrada" como "os dois lados
// concordam".
const KNOWN_STATUSES = new Set<CrmLevelSyncStatus>([
  'reported',
  'already_reported',
  'recently_checked',
  'unchanged',
  'vip_pinned',
]);

export function classifySyncError(
  status: number,
  code: unknown,
): CrmLevelSyncErrorKind {
  if (status === 404) return 'player_not_found';
  if (status === 429) return 'rate_limited';
  if (status === 503 && code === 'lookup_budget_exhausted')
    return 'budget_exhausted';
  if (status === 401) return 'unauthenticated';

  // Inclui o 503 `token_verification_unavailable` (ENG-496: outage do cactus na
  // introspecção do Bearer) e qualquer 422 remanescente — cai no genérico de
  // propósito, a store loga e não retenta nesta sessão.
  return 'api';
}

// POST /crm/users/sync-level — DENUNCIA ao core que o nível que ele guarda
// discorda do que a Smartico reporta. Não pede correção: o core enfileira o id
// pra revisão manual no backoffice e nada volta pra tela.
//
// Autenticação é o Bearer do JOGADOR (getApostatudoAuthHeaders, o mesmo das
// chamadas logadas de promocode/roleta/check-in) — NUNCA o token de integração
// da marca. Identidade vem do JWT do Bearer (gate EnsureUserTokenIdentity,
// ENG-496/497): nada de user_id na URL nem no body — o core ignoraria.
//
// Este adapter não retenta NADA — nem rede, nem 429. Uma tentativa, um veredito.
// O timeout de 10s existe pra soltar a promise, não pra tentar de novo: quem
// chama não espera por ela (ver plugins/crm-level-sync.client.ts).
export class ApostatudoApiPostSyncCrmLevelAdapter
  implements ApiSyncCrmLevelPort
{
  async handle({ userId }: { userId: number }): Promise<CrmLevelSyncResult> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    let response: Response;

    try {
      response = await fetch(`${getApostatudoApiUrl()}/crm/users/sync-level`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          ...getApostatudoAuthHeaders(),
        },
        signal: controller.signal,
      });
    } catch {
      // Timeout (AbortError) ou queda de rede (TypeError).
      throw new CrmLevelSyncError('network');
    } finally {
      clearTimeout(timeoutId);
    }

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));

      throw new CrmLevelSyncError(
        classifySyncError(response.status, err?.error),
        err?.message,
      );
    }

    // 200 ou 201: { user_id, status } (+ `message`, log em inglês do core, e
    // `level` no `unchanged` — nenhum dos dois é lido aqui: `message` nunca vai
    // pra tela e o nível não é mais corrigido por esta rota).
    const body = await response.json().catch(() => ({}));

    return {
      userId: typeof body?.user_id === 'number' ? body.user_id : userId,
      status: KNOWN_STATUSES.has(body?.status)
        ? (body.status as CrmLevelSyncStatus)
        : 'unknown',
    };
  }
}
